<template>
  <div v-bind="$attrs.style" ref="root" v-click-outside="onOuterClickOutside" class="dropdown">
    <button
      class="dropdown__toggler"
      type="button"
      :class="{
        'dropdown__toggler--opened': opened,
        'dropdown__toggler--borderless': borderless,
        [buttonClass]: buttonClass,
      }"
      :disabled="disabled"
      :tabIndex="tabIndex"
      :aria-expanded="opened ? 'true' : 'false'"
      @click="handleToggle"
      @keydown.enter="handleToggle"
    >
      <slot />
    </button>
    <transition :name="preventAnimation ? undefined : 'fade'">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <!-- we should catch click event here, coz we should close dropdown menu only after click event has bubbled here from menu item -->
      <div v-if="opened" class="dropdown__menu" :class="menuClassName" @click="handleMenuClick">
        <slot name="menu" />
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, computed, useContext } from '@nuxtjs/composition-api';
import { Directions, Positions } from '@/components/common/Dropdown/types';

type DropdownProps = {
  menuClass?: string;
  buttonClass?: string | null;
  autoClose?: boolean;
  fullScreenOnMobile?: boolean;
  isVisible?: boolean;
  controlFromParent?: boolean;
  preventAnimation?: boolean;
  disabled?: boolean;
  position?: Positions;
  direction?: Directions;
  borderless?: boolean;
  /**
   * If you want to disable tabbing to the dropdown menu, set this prop to -1 (standard)
   */
  tabIndex?: number | null;
};
type DropdownEmits = {
  (event: 'toggle', payload: boolean): void;
  (event: 'on-close'): void;
};

const props = withDefaults(defineProps<DropdownProps>(), {
  menuClass: '',
  buttonClass: null,
  autoClose: true,
  fullScreenOnMobile: false,
  isVisible: false,
  controlFromParent: false,
  preventAnimation: false,
  disabled: false,
  position: Positions.Left,
  direction: Directions.Bottom,
  borderless: true,
  tabIndex: null,
});
const emit = defineEmits<DropdownEmits>();

const DROPDOWN_MENU_LEFT_PADDING = 16;

const { $device } = useContext();

const root = ref<HTMLElement | null>(null);
const opened = ref(props.isVisible);

const menuClassName = computed(() => [
  {
    'dropdown__menu--right': props.position === Positions.Right,
    'dropdown__menu--top': props.direction === Directions.Top,
    'dropdown__menu--full-screen-mobile': props.fullScreenOnMobile && $device.isMobile,
  },
  props.menuClass,
]);

function adjustPosition() {
  nextTick(() => {
    const dropdownMenuElement = root.value?.querySelector('.dropdown__menu') as HTMLElement;
    const parentRect = root.value?.getBoundingClientRect();
    const dropdownMenuElementRect = dropdownMenuElement?.getBoundingClientRect();

    if (props.position === Positions.Right && (dropdownMenuElementRect?.x ?? 0) < 0 && parentRect) {
      Object.assign(dropdownMenuElement?.style, {
        right: 'auto',
        left: `${-parentRect.x + DROPDOWN_MENU_LEFT_PADDING}px`,
      });
    }
  });
}

function handleToggle() {
  if (props.controlFromParent || props.disabled) return;

  opened.value = !opened.value;
}

function handleClose() {
  // close the dropdown only if it's opened
  if (opened.value || props.isVisible) {
    emit('on-close');

    opened.value = false;
  }
}

function onOuterClickOutside(e: MouseEvent) {
  // if click was inside the dropdown menu, do nothing
  if (e.target instanceof Element && e.target.closest('.dropdown__menu')) return;
  // if click was inside the dropdown toggler, do nothing
  if (e.target instanceof Element && e.target.closest('.dropdown__toggler')) return;

  handleClose();
}

function handleMenuClick() {
  if (props.autoClose) handleClose();
}

watch(opened, newValue => {
  if (newValue) {
    adjustPosition();
  }

  emit('toggle', newValue);
});
watch(
  () => props.isVisible,
  newValue => {
    opened.value = newValue;
  },
);
</script>

<style scoped lang="scss">
.dropdown {
  position: relative;

  &__toggler {
    @include unstyled-button;

    position: relative;
    display: block;
    width: 100%;
    text-align: inherit;
    transition: outline-color 0.2s, border-color 0.2s;
    background: var(--theme-color-card);
    border: 1px solid var(--theme-color-border);
    border-radius: 4px;
    outline: 2px solid transparent;
    outline-offset: 0;

    &--borderless {
      background: transparent;
      border-width: 0;
    }

    &:focus-visible,
    &--opened {
      outline-color: var(--blue-60-a15);
      border-color: var(--blue-60);
    }
  }

  &__menu {
    position: absolute;
    min-width: 224px;
    top: 100%;
    margin-top: 4px;
    z-index: 100;
    left: 0;

    &--right {
      right: 0;
      left: auto;
    }

    &--top {
      top: auto;
      bottom: 100%;
      margin-bottom: 1px;
    }

    &--full-screen-mobile {
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      margin-top: 0;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 150ms;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
